import {
    useLayoutEffect,
    useState,
    type FC
} from "react";
import {
    Platform,
    Animated
} from "react-native";
import type ITabsContentProps from "./type";
import type {
    TabsContentWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    useAnimatedState,
    useReducedMotion,
    getSlideStyle
} from "../../../../helpers/motion";
import type {
    TabsPartContentProps,
    TabsSwapStep
} from "../../type";
import {
    getTabsSwapOffset,
    getTabsID
} from "../../util";
import {
    withTabsContext
} from "../../context";

const TabsContentContent: FC<TabsPartContentProps<ITabsContentProps>> = ({
    customTheme: customThemeProp,
    isKeepMounted = false,
    children,
    context,
    onFocus,
    onBlur,
    style,
    value,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction
    } = NCoreUIKitLocalize.useContext();

    const isReduceMotionEnabled = useReducedMotion();

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDescendantFocusIgnored: true,
        onFocus,
        onBlur
    });

    const isSelected = context.value === value;

    const [
        swapStep,
        setSwapStep
    ] = useState<TabsSwapStep>(0);

    const swapProgress = useAnimatedState({
        onFinish: () => setSwapStep(0),
        value: isSelected ? 1 : 0,
        intent: "contentSwap",
        motion: "fade",
        customTheme
    });

    const {
        container: containerDynamicStyle
    } = useStyles({
        orientation: context.orientation,
        isHidden: !isSelected,
        isFocusVisible,
        shapeConfigs,
        radiuses,
        borders,
        colors
    });

    useLayoutEffect(() => {
        if(isSelected) {
            setSwapStep(context.swapStep);
        }
    }, [
        isSelected
    ]);

    const swapDynamicStyle = getSlideStyle({
        axis: context.orientation === "vertical" ? "y" : "x",
        offset: getTabsSwapOffset({
            orientation: context.orientation,
            step: swapStep,
            direction,
            spaces
        }),
        progress: swapProgress,
        isReduceMotionEnabled
    });

    const webProps: TabsContentWebPropsType = Platform.OS === "web" ? {
        dataSet: interactionHandlers.dataSet,
        "aria-labelledby": getTabsID({
            baseID: context.baseID,
            kind: "trigger",
            value
        }),
        id: getTabsID({
            baseID: context.baseID,
            kind: "content",
            value
        })
    } : {};

    if(!isSelected && !isKeepMounted) {
        return null;
    }

    return <Animated.View
        {...props}
        {...webProps}
        onFocus={interactionHandlers.onFocus}
        onBlur={interactionHandlers.onBlur}
        tabIndex={isSelected ? 0 : -1}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle,
            swapDynamicStyle
        ]}
        role="tabpanel"
    >
        {children}
    </Animated.View>;
};
export default withTabsContext("content", TabsContentContent);
